import { create } from 'zustand'
import { inventoryApi } from '../services'

const VALID_INVENTORY_STATUSES = ['available', 'returned', 'sold', 'sent', 'sending', 'flash']

const useInventoryStore = create((set, get) => ({
  // Inventory data
  items: [], // Full inventory items with case, win, status
  isLoading: false,
  
  // Actions
  setItems: (items) => set({ items }),
  setLoading: (isLoading) => set({ isLoading }),
  
  // Get items grouped by status for tabs (group available and returned together)
  getItemsByStatus: (status) => {
    const { items } = get()
    if (!status || !VALID_INVENTORY_STATUSES.includes(status)) {
      return []
    }
    if (status === 'available') {
      // Include both available and returned items in "available" tab
      return items.filter(item => item.status === 'available' || item.status === 'returned')
    }
    return items.filter(item => item.status === status)
  },
  
  // Get available tab statuses (only non-empty, group available and returned)
  getAvailableStatuses: () => {
    const { items } = get()
    const statusCounts = items.reduce((acc, item) => {
      if (!VALID_INVENTORY_STATUSES.includes(item.status)) {
        return acc
      }

      // Group available and returned under 'available'
      const mappedStatus = item.status === 'returned' ? 'available' : item.status
      acc[mappedStatus] = (acc[mappedStatus] || 0) + 1
      return acc
    }, {})
    
    return Object.keys(statusCounts).filter(status => statusCounts[status] > 0)
  },
  
  // Get status display names (now requires t function from component)
  getStatusDisplayName: (status, t) => {
    if (!status) return null
    
    const statusNames = {
      'available': t('inventory.statusTabAvailable'),
      'returned': t('inventory.statusTabAvailable'), // Объединяем с available
      'sold': t('inventory.statusTabSold'),
      'sent': t('inventory.statusTabSent'),
      'sending': t('inventory.statusTabSending'),
      'flash': t('inventory.statusTabFlash')
    }
    return statusNames[status] || status
  },
  
  // Load inventory from API
  loadInventory: async ({ silent = false } = {}) => {
    try {
      if (!silent) set({ isLoading: true })
      await inventoryApi.getInventory()
      // Response is processed by the universal API handler
    } catch (error) {
      console.error('Failed to load inventory:', error)
    } finally {
      if (!silent) set({ isLoading: false })
    }
  },
  
  // Add item to inventory (usually called from case opening)
  addItem: (item) => {
    const { items } = get()
    set({ items: [...items, item] })
  },
  
  // Remove item from inventory (local state update)
  removeItem: (itemId) => {
    const { items } = get()
    set({ items: items.filter(item => item.id !== itemId) })
  },
  
  // Sell item via API (old method - keeping for compatibility)
  sellItem: async (itemId) => {
    try {
      await inventoryApi.sellItem(itemId)
      // Response is processed by the universal API handler
      // which will update inventory and balance
    } catch (error) {
      console.error('Failed to sell item:', error)
      throw error
    }
  },

  // Sell item via new API endpoint
  saleItem: async (itemId) => {
    try {
      await inventoryApi.saleItem(itemId)
      // Response is processed by the universal API handler
      // which will update inventory and balance
    } catch (error) {
      console.error('Failed to sell item:', error)
      throw error
    }
  },

  // Sell all available items at once
  saleAllItems: async () => {
    try {
      await inventoryApi.saleAllItems()
      // Response is processed by the universal API handler
      // which will update inventory and balance
    } catch (error) {
      console.error('Failed to sell all items:', error)
      throw error
    }
  },

  // Send item to Steam
  sendItem: async (itemId) => {
    try {
      const response = await inventoryApi.sendItem(itemId)
      if (response.error) throw new Error(response.error)
      if (response.send?.withdraw) {
        set({ items: get().items.map(item => item.id === itemId
          ? { ...item, status: 'sending', withdraw: response.send.withdraw }
          : item) })
      }
      // Response is processed by the universal API handler
      // which will update inventory status
      return response
    } catch (error) {
      console.error('Failed to send item:', error)
      throw error
    }
  },
  
  // Withdraw item via API
  withdrawItem: async (itemId, tradeUrl) => {
    try {
      await inventoryApi.withdrawItem(itemId, tradeUrl)
      // Response is processed by the universal API handler
    } catch (error) {
      console.error('Failed to withdraw item:', error)
      throw error
    }
  },
  
  // Cancel withdrawal
  cancelWithdrawal: async (itemId) => {
    try {
      await inventoryApi.cancelWithdrawal(itemId)
      // Response is processed by the universal API handler
    } catch (error) {
      console.error('Failed to cancel withdrawal:', error)
      throw error
    }
  },
  
  // Update item status locally (for optimistic updates)
  updateItemStatus: (itemId, status) => {
    const { items } = get()
    const updatedItems = items.map(item => 
      item.id === itemId ? { ...item, status } : item
    )
    set({ items: updatedItems })
  },
  
  clearInventory: () => set({ items: [] })
}))

export default useInventoryStore
